import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";

/**
 * Sprint 26.0 — continuous-improvement loop, repointed to tasks. The two manual
 * run actions surfaced on a task's Continuous Improvement section: compose a
 * retrospective and apply learnings (check the task against accumulated
 * feedback). Both wrap MCP tools and refresh the task + content caches so the
 * chrome updates.
 */

export interface RunRetrospectiveResult {
  task_id: string;
  agent_id: string;
  retrospective_page_id: string;
  retrospective_page_name: string;
  feedback_memory_ids: string[];
  evidence_counts: { observation_memories: number; linked_pages: number };
  thin: boolean;
}

export interface RelevantLearning {
  id: string;
  name: string;
  confidence: number | null;
  similarity_score: number | null;
  snippet: string;
  is_pattern: boolean;
}

export interface CheckLearningsResult {
  task_id: string;
  agent_id: string;
  checked_at: string;
  findings_count: number;
  significant: boolean;
  relevant_memories: RelevantLearning[];
}

function unwrap<T>(r: { isError?: boolean; structuredContent?: { result?: T }; content?: Array<{ text?: string }> }): T {
  if (r.isError) throw new Error(r.content?.[0]?.text ?? "Request failed");
  const result = r.structuredContent?.result;
  if (!result) throw new Error("Empty response");
  return result;
}

/** Invalidate everything a compose/check touches: tasks + pages + memory lists. */
function invalidateLoopCaches(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["agent-tasks"] });
  qc.invalidateQueries({ queryKey: ["agent-brain-snapshot"] });
  qc.invalidateQueries({ queryKey: ["child-nodes"] });
  qc.invalidateQueries({ queryKey: queryKeys.workspacePages() });
}

export function useRunRetrospective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ task_id, template }: { task_id: string; template?: string }) => {
      const r = await mcpClient.toolsCall<{ result?: RunRetrospectiveResult }>("compose_retrospective", {
        task_id,
        ...(template ? { template } : {}),
      });
      return unwrap<RunRetrospectiveResult>(r);
    },
    onSuccess: () => invalidateLoopCaches(qc),
  });
}

export function useApplyLearnings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ task_id }: { task_id: string }) => {
      const r = await mcpClient.toolsCall<{ result?: CheckLearningsResult }>("check_task_against_learnings", {
        task_id,
      });
      return unwrap<CheckLearningsResult>(r);
    },
    onSuccess: () => invalidateLoopCaches(qc),
  });
}
